import type { Cliente, DadosNegocio, Site, SiteSpec } from '@nyroh/contracts';
import { paraSlug, usuarioPadraoDoSlug } from '@nyroh/contracts';

import type { ClientData, ClientEntity } from '../types';

/**
 * ============================================================================
 * PONTE ENTRE O MODELO NOVO E OS TIPOS DAS TELAS
 * ============================================================================
 *
 * As telas do painel — `ClientDashboard.tsx` (1.338 linhas),
 * `ClientDossierManager.tsx` (1.321) e mais uma duzia — falam em `ClientEntity`,
 * com o JSONB `data` inteiro dentro. Reescrever todas de uma vez, sem teste
 * automatizado no repositorio, seria trocar um problema por outro.
 *
 * Entao a conversao mora AQUI, num arquivo so, e `services/db.ts` continua
 * expondo exatamente a mesma superficie de antes. As telas nao mudam; o que muda
 * e de onde o dado vem.
 *
 * O que a ponte esconde de proposito:
 *   `data.pin`      — nao existe mais. O PIN virou hash argon2id em tabela
 *                     propria, verificado so no servidor. Nada aqui pode
 *                     reconstruir esse campo, e essa e a intencao.
 *   `htmlContent`   — nao vem mais do banco. Vem do R2, e so quando pedido:
 *                     numa listagem ele fica vazio, porque listagem nunca deve
 *                     arrastar o artefato.
 */

export interface ClienteComNegocio extends Cliente {
  dadosNegocio?: DadosNegocio | null;
}

/**
 * Modelo novo -> tipo que as telas ja usam.
 *
 * `site` e opcional porque a LISTAGEM nao carrega o site de cada cliente: seria
 * uma consulta por linha da tabela. Quem precisa do endereco exato e da versao
 * corrente passa por `getClientById`, que carrega.
 */
export function paraClientEntity(
  c: ClienteComNegocio,
  htmlContent = '',
  site?: Pick<Site, 'id' | 'urlPublica' | 'versaoAtualId'> | null,
): ClientEntity {
  const n = c.dadosNegocio;
  const data: ClientData = {
    id: c.id,
    campaignId: n?.campanhaId ?? '',
    targetId: n?.alvoId ?? '',
    name: c.nome,
    address: c.endereco ?? '',
    rating: c.nota ?? 0,
    reviewCount: c.totalAvaliacoes ?? 0,
    website: n?.website ?? null,
    categories: n?.categorias ?? (c.categoria ? [c.categoria] : []),
    topReviews: (n?.avaliacoesTopo as unknown[]) ?? [],
    summary: n?.resumo ?? '',
    blueprint: n?.blueprint ?? [],
    placeUri: n?.placeUri ?? '',
    phoneNumber: c.telefone ?? '',
    openingHours: n?.horarios ?? null,
    businessStatus: n?.statusNegocio ?? '',
    priceLevel: n?.faixaPreco ?? null,
    amenities: n?.comodidades ?? [],
    photoAnalysis: n?.analiseFotos ?? '',
    seoKeywords: n?.palavrasChaveSeo ?? [],
    createdAt: c.criadoEm,
    // `username` continua visivel (e o login do portal); `pin` NAO — ele nao
    // existe mais em lugar nenhum que o navegador alcance.
    //
    // O valor era o slug CRU, e isso estava errado: o usuario gravado em
    // `credenciais_cliente` e o slug SEM os hifens. A tela de login com marca
    // pre-preenchia o campo com um usuario que nao existe, e o dono do negocio
    // via "usuario ou senha incorretos" sem ter digitado nada. Agora os dois
    // lados derivam pela mesma funcao, que mora no contrato.
    username: usuarioPadraoDoSlug(c.slug),
  };

  return {
    id: c.id,
    data,
    htmlContent,
    slug: c.slug,
    // O painel so conhece 'pending' | 'active'. Os cinco status do modelo novo
    // sao mapeados para esses dois; a granularidade extra vive no servidor.
    status: c.status === 'ativo' ? 'active' : 'pending',
    customDomain: c.dominioPrincipal,
    customPrice: c.precoPersonalizado,
    siteId: site?.id ?? c.siteId ?? null,
    urlPublica: site?.urlPublica ?? null,
    versaoAtualId: site?.versaoAtualId ?? null,
  };
}

/** Tipo das telas -> corpo aceito por `clientes.criar` / `clientes.importar`. */
export function paraCriarCliente(e: ClientEntity) {
  const d = e.data ?? ({} as ClientData);
  return {
    nome: d.name || e.slug,
    // Ultima rede antes do contrato: entidade montada por outro caminho (import
    // antigo, tela que ainda nao passou por `paraSlug`) e normalizada aqui, em
    // vez de virar erro de validacao no cliente HTTP com o lote inteiro junto.
    slug: paraSlug(e.slug),
    telefone: d.phoneNumber || undefined,
    endereco: d.address || undefined,
    categoria: d.categories?.[0] || undefined,
    nota: typeof d.rating === 'number' ? d.rating : undefined,
    totalAvaliacoes: typeof d.reviewCount === 'number' ? d.reviewCount : undefined,
    status: (e.status === 'active' ? 'ativo' : 'pendente') as 'ativo' | 'pendente',
    precoPersonalizado: e.customPrice ?? undefined,
    dadosNegocio: {
      placeUri: d.placeUri || null,
      website: d.website ?? null,
      horarios: d.openingHours ?? null,
      avaliacoesTopo: d.topReviews ?? [],
      categorias: d.categories ?? [],
      comodidades: d.amenities ?? [],
      palavrasChaveSeo: d.seoKeywords ?? [],
      resumo: d.summary || null,
      analiseFotos: d.photoAnalysis || null,
      blueprint: d.blueprint ?? [],
      faixaPreco: d.priceLevel ?? null,
      statusNegocio: d.businessStatus || null,
      campanhaId: d.campaignId || null,
      alvoId: d.targetId || null,
    },
    // Sem `pin`: quando ausente, o servidor gera um com CSPRNG e devolve UMA vez.
    credencial: d.username ? { usuario: d.username } : undefined,
  };
}

/**
 * Spec minimo que embrulha HTML pronto.
 *
 * O editor de codigo do painel (`ClientModal`, `useIframeEditor`) trabalha com a
 * string de HTML inteira. Em vez de reescrever esses componentes agora, o HTML
 * entra como uma unica secao `htmlBruto` — o mesmo caminho que a migracao do
 * acervo usa. Assim o editor continua funcionando E o artefato passa a morar no
 * R2, que era o objetivo.
 *
 * Sites NOVOS devem nascer com spec de verdade (secoes tipadas). Este caminho e
 * a ponte do acervo, nao o destino.
 */
export function specDeHtmlBruto(
  html: string,
  base: { nome: string; slug: string; titulo?: string; descricao?: string },
): SiteSpec {
  return {
    versaoSpec: 1,
    template: 'html-importado',
    // `classico` porque este caminho NAO usa o leiaute: a unica secao e
    // `htmlBruto` com o documento inteiro, e o renderizador serve o documento
    // como esta, sem envelope e sem CSS proprio. Qualquer outro valor aqui
    // seria decorativo e enganoso.
    leiaute: 'classico',
    negocio: {
      nome: base.nome,
      categoria: '',
      telefone: '',
      whatsapp: '',
      endereco: '',
    },
    paleta: {
      primaria: '#1F4E79',
      secundaria: '#16324F',
      fundo: '#FFFFFF',
      texto: '#1A1A1A',
    },
    tipografia: { titulo: 'Inter', corpo: 'Inter', escala: 'normal' },
    seo: {
      titulo: base.titulo ?? base.nome,
      descricao: base.descricao ?? '',
      palavrasChave: [],
      indexavel: true,
    },
    secoes: [{ tipo: 'htmlBruto', html, origem: 'editor-manual' }],
    cssExtra: '',
  };
}

/** Extrai o HTML de um spec que so tem `htmlBruto` — o caminho inverso. */
export function htmlDoSpec(spec: SiteSpec): string | null {
  const bruta = spec.secoes.find((s) => s.tipo === 'htmlBruto');
  return bruta && bruta.tipo === 'htmlBruto' ? bruta.html : null;
}
